<template>
  <div class="board-view-container">
    <!-- Initial Loading State (only on first load if taking > 1 second) -->
    <div v-if="showInitialLoader" class="row">
      <div class="col-md-12">
        <div class="c-card text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-2 mb-0">Loading board data...</p>
        </div>
      </div>
    </div>

    <!-- Board Content -->
    <div v-else class="row board-content-row">
      <div class="col-md-12">
        <div class="board-wrapper">
          <div class="board-scroll-container">
            <BoardColumn
              v-for="column in filteredBoardColumns"
              :key="column.id"
              :column="column"
              :enable-settings="true"
              :selected-job="selectedJob"
              @job-click="onJobClick"
              @job-drop="onJobDrop"
              @job-drag-start="onJobDragStart"
              @open-column-settings="onOpenColumnSettings"
            />
          </div>
        </div>
      </div>
    </div>

    <BoardColumnSettingsModal
      v-if="activeColumn"
      :modal-id="settingsModalId"
      :column="activeColumn"
      :board-type="boardType"
      :used-status-ids="computeUsedStatusIds(activeColumn)"
      @save="onSaveColumnSettings"
      @closed="activeColumn=null"
      ref="settingsModal"
    />

    <!-- Status Selection Modal -->
    <SelectStatusModal
      v-if="pendingStatusSelection"
      :modal-id="statusSelectionModalId"
      :job="pendingStatusSelection.job"
      :available-statuses="getAvailableStatusesForColumn(pendingStatusSelection.targetColumnId)"
      :target-column-id="pendingStatusSelection.targetColumnId"
      @status-selected="onStatusSelected"
      ref="statusSelectionModal"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import BoardColumn from './BoardColumn.vue';
import BoardColumnSettingsModal from './BoardColumnSettingsModal.vue';
import SelectStatusModal from '../_modals/select-status-modal.vue';
import { JOBS_BOARD_CONFIG, getDefaultStatusForColumn } from '@/config/board-config';

export default {
  name: 'BoardView',
  props: {
    boardType: { type: String, default: 'jobs' } // New prop to specify which board type
  },
  components: {
    BoardColumn,
    BoardColumnSettingsModal,
    SelectStatusModal
  },
  computed: {
    ...mapGetters('board', ['filteredBoardColumns', 'isLoading', 'selectedJob']),
    ...mapGetters('boardSettings', ['isLoaded']),
    
    showInitialLoader() {
      // Only show loading on first load if it's taking more than 1 second
      return this.isInitialLoad && this.showLoader;
    }
  },
  data() {
    return {
      // Initial loading state
      isInitialLoad: true,
      showLoader: false,
      loaderTimeout: null,
      // Drag and drop state
      draggedJob: null,
      sourceColumnId: null,
      // Column settings state
      activeColumn: null,
      settingsModalId: 'board-column-settings-modal',
      // Status selection state
      pendingStatusSelection: null,
      statusSelectionModalId: 'board-status-selection-modal',
      // Loading state for status updates
      isUpdatingStatus: false
    };
  },
  methods: {
    ...mapActions('board', [
      'selectJob',
      'updateJobStatus',
      'animateJobMove',
      'initializeBoard',
      'refreshBoard'
    ]),
    ...mapActions('boardSettings', [
      'initializeBoardSettings',
      'updateColumnSettings',
      'setColumnDefaultStatus'
    ]),

    onJobClick(job) {
      this.selectJob(job);
      this.$emit('job-selected', job);
    },

    onJobDragStart(data) {
      this.draggedJob = data.job;
      this.sourceColumnId = data.columnId;
    },

    async onJobDrop(data) {
      if (!this.draggedJob || !this.sourceColumnId) return;

      // Don't allow drop on same column
      if (this.sourceColumnId === data.columnId) {
        this.resetDragState();
        return;
      }

      // Check if target column has a default status assigned
      const targetColumn = this.filteredBoardColumns.find(col => col.id === data.columnId);
      if (!targetColumn) {
        this.resetDragState();
        return;
      }

      // Get default status for this column
      const defaultStatus = getDefaultStatusForColumn(data.columnId, JOBS_BOARD_CONFIG);
      
      // If no default status ID is configured, show status selection modal
      if (!defaultStatus.statusId || defaultStatus.statusId === 0) {
        this.pendingStatusSelection = {
          job: this.draggedJob,
          targetColumnId: data.columnId,
          sourceColumnId: this.sourceColumnId
        };
        
        // Show the status selection modal
        this.$nextTick(() => {
          this.$bvModal.show(this.statusSelectionModalId);
        });
        
        return; // Don't reset drag state yet, we'll do it after status selection
      }

      // Proceed with the move using the default status
      await this.performJobMove(data.columnId, defaultStatus.statusName, defaultStatus.statusId);
    },

    async onStatusSelected(statusSelection) {
      // User selected a status from the modal
      await this.performJobMove(
        statusSelection.targetColumnId,
        statusSelection.statusName,
        statusSelection.statusId
      );

      // If user opted to auto-assign future cards, save this as the default status for the column
      if (statusSelection.autoAssignFuture) {
        await this.setColumnDefaultStatus({
          boardType: 'jobs', // Assuming this is the jobs board - you may need to make this dynamic
          columnId: statusSelection.targetColumnId,
          statusId: statusSelection.statusId,
          statusName: statusSelection.statusName
        });
      }
      
      // Clear pending selection
      this.pendingStatusSelection = null;
    },

    async performJobMove(targetColumnId, statusName, statusId) {
      try {
        // Set loading state
        this.isUpdatingStatus = true;

        // Animate the move
        this.animateJobMove({
          jobId: this.draggedJob.id,
          fromColumnId: this.sourceColumnId,
          toColumnId: targetColumnId
        });

        // Update job status
        await this.updateJobStatus({
          jobId: this.draggedJob.id,
          newStatus: statusName,
          newStatusId: statusId,
          newColumnId: targetColumnId
        });

        // Refresh board data to ensure it's up to date after successful API update
        await this.refreshBoard();

        // Show success notification
        this.$bvToast.toast(`Job ${this.draggedJob.id} status updated to "${statusName}"`, {
          title: 'Status Updated',
          variant: 'success',
          autoHideDelay: 2000
        });

        // Emit event for parent component
        this.$emit('job-moved', {
          job: this.draggedJob,
          fromColumnId: this.sourceColumnId,
          toColumnId: targetColumnId,
          newStatus: statusName,
          newStatusId: statusId
        });

      } catch (error) {
        // Error moving job - handled by toast notification
        this.$bvToast.toast('Failed to move job. Please try again.', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 3000
        });
      } finally {
        // Clear loading state and reset drag state
        this.isUpdatingStatus = false;
        this.resetJobCardDragState();
        this.resetDragState();
      }
    },

    resetJobCardDragState() {
      // Find and reset the dragging job card's visual state
      if (this.draggedJob) {
        this.$nextTick(() => {
          // Find the job card element by data attribute
          const jobCardElement = this.$el.querySelector(`[data-job-id="${this.draggedJob.id}"]`);
          if (jobCardElement && jobCardElement.__vue__) {
            // Call the resetDragState method on the JobCard component
            jobCardElement.__vue__.resetDragState();
          }
        });
      }
    },

    resetDragState() {
      this.draggedJob = null;
      this.sourceColumnId = null;
    },

    onOpenColumnSettings(column) {
      this.activeColumn = { ...column };
      this.$nextTick(() => {
        this.$refs.settingsModal && this.$refs.settingsModal.show();
      });
    },

    onSaveColumnSettings(updatedData) {
      // Use centralized board settings store
      this.updateColumnSettings({
        boardType: updatedData.boardType,
        columnId: updatedData.columnId,
        updatedColumn: updatedData.column
      });
      
      this.activeColumn = null;
      
      // Show success message
      this.$bvToast.toast('Column settings updated successfully', {
        title: 'Settings Saved',
        variant: 'success',
        autoHideDelay: 2000
      });
    },

    computeUsedStatusIds(current) {
      const ids = new Set();
      this.filteredBoardColumns.forEach(col => {
        if (!col || col === current) return;
        if (Array.isArray(col.statusIds)) col.statusIds.forEach(id => ids.add(id));
      });
      return Array.from(ids);
    },

    getAvailableStatusesForColumn(columnId) {
      // For now, return all available project statuses
      // This could be filtered based on business rules or column configuration
      return this.$store.getters['board/getProjectStatusList'] || [];
    }
  },

  async mounted() {
    // Initialize board settings store first
    if (!this.isLoaded) {
      await this.initializeBoardSettings();
    }
    
    // Set timeout to show loader only if loading takes more than 1 second
    this.loaderTimeout = setTimeout(() => {
      if (this.isInitialLoad) {
        this.showLoader = true;
      }
    }, 1000);

    try {
      // Initialize the board with real data
      await this.initializeBoard();
    } catch (error) {
      // Error initializing board - handled by toast notification
      this.$bvToast.toast('Failed to load board data. Please refresh the page.', {
        title: 'Error',
        variant: 'danger',
        autoHideDelay: 5000
      });
    }
  },

  beforeDestroy() {
    // Clear timeout if component is destroyed
    if (this.loaderTimeout) {
      clearTimeout(this.loaderTimeout);
    }
  },

  watch: {
    // Watch for when data is loaded to hide initial loading state
    filteredBoardColumns: {
      handler(newColumns) {
        if (newColumns && newColumns.length > 0 && this.isInitialLoad) {
          this.isInitialLoad = false;
          this.showLoader = false;
          if (this.loaderTimeout) {
            clearTimeout(this.loaderTimeout);
            this.loaderTimeout = null;
          }
        }
      },
      immediate: true
    },

    // Watch for loading state changes
    isLoading: {
      handler(newLoading) {
        if (!newLoading && this.isInitialLoad) {
          this.isInitialLoad = false;
          this.showLoader = false;
          if (this.loaderTimeout) {
            clearTimeout(this.loaderTimeout);
            this.loaderTimeout = null;
          }
        }
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
/* Board Layout Styles */
.board-view-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.board-wrapper {
  background: transparent;
  padding: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  overflow-x: hidden;
}

.board-scroll-container {
  display: flex;
  gap: 1rem;
  overflow-x: hidden;
  padding-bottom: 0.5rem;
  overflow-y: hidden;
  width: 100%;
}

/* Full-height board layout improvements */
.board-content-row {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.board-content-row .col-md-12 {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Responsive adjustments for smaller screens */
@media (max-width: 768px) {
  .board-scroll-container {
    gap: 0.75rem;
  }
}

@media (max-width: 576px) {
  .board-scroll-container {
    gap: 0.5rem;
  }
}

/* Ensure boards work in tab context */
.tab-pane {
  height: 100%;
}

.tab-content {
  height: 100%;
}
</style>
